import React from 'react';
import { Box, Paper, Typography, Chip, Stack, LinearProgress, Link } from '@mui/material';
import { Warning as WarnIcon } from '@mui/icons-material';
import { TrainingInsights, scoreColor, formatScore, barColor } from '../types';

interface Props {
  data: TrainingInsights['landmineHandling'];
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

const LandmineHandlingSection: React.FC<Props> = ({ data, onNavigateToTimestamp }) => {
  if (!data) return null;

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Landmine Handling</Typography>
        <Chip
          label={formatScore(data.overallScore)}
          color={scoreColor(data.overallScore)}
          size="medium"
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {data.overallSummary}
      </Typography>
      {data.overallScore != null && (
        <LinearProgress
          variant="determinate"
          value={data.overallScore}
          color={barColor(data.overallScore)}
          sx={{ mb: 3, height: 8, borderRadius: 1 }}
        />
      )}

      <Stack spacing={2}>
        {data.items?.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <WarnIcon
                color={item.wasTriggered ? 'error' : 'success'}
                fontSize="small"
              />
              <Typography variant="subtitle2">
                {item.trigger}
              </Typography>
              <Chip
                label={item.wasTriggered ? 'triggered' : 'avoided'}
                size="small"
                color={item.wasTriggered ? 'error' : 'success'}
                variant="outlined"
              />
              <Chip
                label={`${item.handlingScore}%`}
                size="small"
                color={scoreColor(item.handlingScore)}
              />
              {item.timestampIso && onNavigateToTimestamp && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onNavigateToTimestamp(item.timestampIso!)}
                  sx={{ ml: 'auto' }}
                >
                  View in timeline &rarr;
                </Link>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {item.handlingSummary}
            </Typography>
            {item.whatRepSaid && (
              <Typography variant="caption" display="block" color="text.secondary">
                <strong>Rep said:</strong> "{item.whatRepSaid}"
              </Typography>
            )}
            {item.recommendedResponse && (
              <Box mt={1} p={1.5} bgcolor="info.50" borderRadius={1}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  Recommended response
                </Typography>
                <Typography variant="body2">{item.recommendedResponse}</Typography>
              </Box>
            )}
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
};

export default LandmineHandlingSection;
